import { Box, Flex, Text } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import {
  FiActivity,
  FiCpu,
  FiHome,
  FiMapPin,
  FiSettings,
  FiShield,
  FiWifi,
} from "react-icons/fi"

import { RouterLink } from "@/components/ui/router-link"

import type { UserPublic } from "@/client"
import { IconBox } from "@/components/VisionUI"
import { useColorModeValue } from "@/components/ui/color-mode"
import { ReactIcon } from "@/components/ui/icon"
import { useDemoMode } from "@/hooks/useDemoMode"
import { colors, effects } from "@/theme"
import type { IconType } from "react-icons/lib"

interface SidebarItemDef {
  icon: IconType
  titleKey: string
  path: string
}
const itemDefs: SidebarItemDef[] = [
  { icon: FiHome, titleKey: "nav.dashboard", path: "/" },
  { icon: FiWifi, titleKey: "nav.readers", path: "/readers" },
  { icon: FiCpu, titleKey: "nav.tags", path: "/tags" },
  { icon: FiMapPin, titleKey: "nav.tagMap", path: "/tag-map" },
  { icon: FiActivity, titleKey: "nav.events", path: "/events" },
  { icon: FiSettings, titleKey: "nav.settings", path: "/settings" },
]

interface SidebarItemsProps {
  onClose?: () => void
  collapsed?: boolean
}

const SidebarItems = ({ onClose, collapsed = false }: SidebarItemsProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const location = useLocation()
  const { enabled: demoEnabled } = useDemoMode()
  const textColor = useColorModeValue("#1a202c", "white")
  const inactiveBg = useColorModeValue("#EDF2F7", effects.glass.inactiveBg)
  const inactiveIconColor = useColorModeValue("#4A5568", "white")

  // Show the Admin link to superusers always, and to everyone in demo mode
  // so visitors can reach /admin and the DPP sub-pages. Restricted tiles
  // on /admin self-protect via the "sign in as administrator" dialog
  // (see _layout/admin.tsx), and the matching backend gate is
  // ``forbid_in_demo_mode_unless_superuser`` so a logged-in admin can
  // actually do the work.
  const showAdmin = currentUser?.is_superuser || demoEnabled
  const finalItems: SidebarItemDef[] = [...itemDefs]
  if (showAdmin) {
    finalItems.push({ icon: FiShield, titleKey: "nav.admin", path: "/admin" })
  }
  // Licenses is reached from the Admin page (it has a Licenses card), so it is
  // no longer a standalone sidebar item.

  const listItems = finalItems.map(({ icon, titleKey, path }) => {
    const isActive =
      location.pathname === path ||
      (path === "/admin" &&
        (location.pathname === "/organisations" ||
          location.pathname === "/user-management" ||
          location.pathname.startsWith("/user-permissions") ||
          location.pathname === "/tag-templates" ||
          location.pathname === "/licenses" ||
          location.pathname === "/admin-audit")) ||
      (path === "/readers" && location.pathname.startsWith("/reader")) ||
      (path === "/tags" &&
        (location.pathname.startsWith("/tag") ||
          location.pathname.startsWith("/template")) &&
        location.pathname !== "/tag-templates" &&
        location.pathname !== "/tag-map")
    const itemContent = (
      <Box display="block" w="100%">
        <Flex gap="14px" py="15px" px="15px" onClick={onClose} align="center">
          <IconBox
            bg={isActive ? colors.brand[200] : inactiveBg}
            color={isActive ? "white" : inactiveIconColor}
            h="30px"
            w="30px"
            me="12px"
            transition="all 0.2s linear"
          >
            <ReactIcon
              icon={icon}
              color={isActive ? "white" : inactiveIconColor}
              fontSize="18px"
            />
          </IconBox>
          <Text
            fontWeight="500"
            fontSize="sm"
            color={textColor}
            display={collapsed ? "none" : "block"}
          >
            {t(titleKey)}
          </Text>
        </Flex>
      </Box>
    )

    return (
      <RouterLink to={path} key={titleKey} asChild>
        {itemContent}
      </RouterLink>
    )
  })

  return (
    <Flex direction="column" w="100%">
      {listItems}
    </Flex>
  )
}

export default SidebarItems
